// ANA-R1-02 — single effective AI-finding set, consumed identically by the
// List, Tiles and Viewer widgets (AC1).
//
// The 2026-06-06 walkthrough found the three widgets disagreeing about which
// AI findings exist on the current image: the List, Tiles and Viewer each
// derived their finding set through a slightly different path, so counts and
// bounding boxes diverged. Christian: *"all three widget must show the same
// amount of information"* [00:15:37], and *"in the list we show everything
// and I assume in a big viewer we just show if the confidence is above 50%"*
// [00:15:25] — i.e. per-widget narrowing the user never asked for.
//
// This hook is the ONE place the effective finding set is derived:
//
//   * AI-class confidence narrowing comes from `useEffectiveAiClassFilter`
//     (the committed AI Filter Module state, gated to the AI kind exactly as
//     the data-point counts are). Absent ⇒ no narrowing ⇒ ALL findings of the
//     image (AC2 — no hidden per-widget threshold, no implicit 35%/50%
//     default).
//   * NO `state` (pending/dismissed/promoted) narrowing is applied here — the
//     effective set spans every state so the three widgets agree out of the
//     box (AC3). The List's segmented state control composes on top as a
//     List-only view refinement; it does not define which findings "exist".
//
// Opening or closing the AI Filter Module does not mutate the inputs to this
// hook (the module edits `config`; only the debounced `committedFilter` —
// already null when nothing is checked — feeds the queries), so the module is
// inert until the user actually checks a class (AC4). Changing a checked
// class's min-confidence still applies immediately via the same committed
// filter (AC5).
import { useMemo } from "react";
import type { AiFinding, GetFindingsParams } from "@aeriosiq/types";
import { useFindingsQuery } from "../findings/queries.js";
import { useEffectiveAiClassFilter } from "./useEffectiveAiClassFilter.js";

export interface EffectiveFindingSet {
  /** The findings query (shared key — TanStack dedups across widgets). */
  query: ReturnType<typeof useFindingsQuery>;
  /** The effective visible findings (image-scoped when `imageId` is set). */
  findings: AiFinding[];
  /** Ids of the effective visible findings, for parity / selection checks. */
  findingIds: Set<string>;
}

export interface UseEffectiveFindingSetOptions {
  /**
   * When set, the set is scoped to a single image (the Viewer overlay + the
   * List/Tiles "Current image" view). When absent, the set is dataset-wide
   * (the selection reconciler + the tab badge denominator).
   */
  imageId?: string | null;
  /**
   * Page size for the underlying query. Defaults to 200 — the per-image worst
   * case is well under that, and the dataset-wide callers that need the full
   * set (selection reconciler) pass a larger value and check completeness.
   */
  pageSize?: number;
}

/**
 * PERF-06 (audit I-6) — canonical params for the effective-finding-set
 * query. The mount path (`useEffectiveFindingSet` below) and the overlay
 * prefetch (`useOverlayPrefetch`) both build their params through THIS
 * helper, so the prefetched query key is structurally identical to the one
 * the Viewer/List/Tiles mount — key drift = cache miss = wasted prefetch.
 */
export function effectiveFindingSetParams(options: {
  imageId?: string | null;
  pageSize?: number;
  /** The committed AI-class filter (from `useEffectiveAiClassFilter`). */
  aiClassFilter: string | null;
}): GetFindingsParams {
  const { imageId = null, pageSize = 200, aiClassFilter } = options;
  return {
    pageSize,
    ...(imageId ? { imageId } : {}),
    ...(aiClassFilter ? { aiClassFilter } : {}),
  };
}

/**
 * Derive the single effective AI-finding set for a data acquisition. All three
 * finding-rendering surfaces call this so their narrowing is byte-identical:
 * same `aiClassFilter`, same (absent) `state`, same image scope.
 */
export function useEffectiveFindingSet(
  dataAcquisitionId: string,
  options: UseEffectiveFindingSetOptions = {},
): EffectiveFindingSet {
  const { imageId = null, pageSize = 200 } = options;
  const aiClassFilter = useEffectiveAiClassFilter();

  const query = useFindingsQuery(
    dataAcquisitionId,
    effectiveFindingSetParams({ imageId, pageSize, aiClassFilter }),
  );

  const findings = useMemo<AiFinding[]>(() => {
    const all = query.data?.items ?? [];
    // When image-scoped, the BE already filters by `imageId`; the extra guard
    // keeps the set honest if a stale page from another image lingers in the
    // cache between the param change and the refetch.
    return imageId ? all.filter((f) => f.imageId === imageId) : all;
  }, [query.data?.items, imageId]);

  const findingIds = useMemo(() => {
    const ids = new Set<string>();
    for (const f of findings) ids.add(f.id);
    return ids;
  }, [findings]);

  return { query, findings, findingIds };
}
